import { useCallback } from 'react';
import { useCreateRegistryPayoutAccountSessionMutation } from './PayoutBankAccountDialog.graphql.generated';
import type { PayoutAccountSession } from './PayoutBankAccountDialog.types';

/**
 * Supplies `PayoutBankAccountDialog` with a session from the registry graph.
 *
 * `fetchPolicy` deliberately isn't set: this is a mutation, so Apollo never serves it from cache —
 * which is the whole reason the server exposes this as a mutation rather than a field on
 * `RegistryPayoutAccount`. A cached client secret is a dead client secret.
 *
 * A cash-funds equivalent will sit beside this one, feeding the same dialog.
 */
export const useRegistryPayoutAccountSession = (eventId: string) => {
  const [createSession] = useCreateRegistryPayoutAccountSessionMutation();

  return useCallback(async (): Promise<PayoutAccountSession> => {
    const { data, errors } = await createSession({ variables: { eventId } });

    // the payload is non-nullable server-side, so an absent session means the request failed --
    // most likely the account is no longer `readyToUse`
    if (errors?.length || !data?.createRegistryPayoutAccountSession) {
      throw new Error(errors?.[0]?.message ?? 'Failed to create a payout account session');
    }

    const { clientSecret, publishableKey } = data.createRegistryPayoutAccountSession;
    return { clientSecret, publishableKey };
  }, [createSession, eventId]);
};
